/**
 * <s-gallery-lightbox> — full-screen, zoomable photo viewer for the customer gallery page.
 *
 * Photo tiles open it with commandfor="<this id>" command="--open"; the viewer shows the
 * tile's photo at full resolution and steps through the tiles that are currently visible
 * (only the chosen gallery panel), so headlights and tail lights stay separate.
 *
 * Commands: --open, --close, --previous, --next, --zoom-in, --zoom-out.
 * Zoom: mouse wheel, double-click / double-tap, two-finger pinch, +/- keys and buttons.
 * Pan: drag while zoomed. Swipe left/right while not zoomed to change photo.
 *
 * Required markup:
 *   dialog                       — native modal surface.
 *   [data-lightbox-viewport]     — pointer surface holding the image.
 *   img[data-lightbox-image]     — the large photo; transformed for zoom/pan.
 *   [data-lightbox-counter]      — "3 / 40" position text.
 *   [data-lightbox-scale]        — zoom percentage text.
 *   [data-lightbox-zoom-in|out]  — zoom buttons (disabled at the limits).
 *   [data-lightbox-nav]          — previous/next buttons (hidden for a single photo).
 */
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const STEP = 1.5;
const DOUBLE_TAP_SCALE = 2.5;
const LARGE_WIDTHS = [1100, 1500, 2000, 2400, 3000];

class SGalleryLightbox extends HTMLElement {
  #ac;
  #dialog;
  #viewport;
  #image;
  #items = [];
  #index = 0;
  #trigger = null;
  #scale = 1;
  #x = 0;
  #y = 0;
  #pointers = new Map();
  #gesture = null;
  #lastTap = 0;
  #moved = false;
  #loadToken = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#viewport = this.querySelector('[data-lightbox-viewport]');
    this.#image = this.querySelector('img[data-lightbox-image]');
    if (!this.#dialog || !this.#viewport || !this.#image) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#onCommand, opts);
    this.#dialog.addEventListener('keydown', this.#onKeydown, opts);
    this.#dialog.addEventListener('close', this.#onClose, opts);
    this.#viewport.addEventListener('wheel', this.#onWheel, { ...opts, passive: false });
    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, opts);
    this.#viewport.addEventListener('pointermove', this.#onPointerMove, opts);
    this.#viewport.addEventListener('pointerup', this.#onPointerUp, opts);
    this.#viewport.addEventListener('pointercancel', this.#onPointerUp, opts);
    this.#viewport.addEventListener('dblclick', this.#onDoubleClick, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onCommand = (event) => {
    switch (event.command) {
      case '--open':
        this.#open(event.source);
        break;
      case '--close':
        this.#dialog.close();
        break;
      case '--previous':
        this.#go(-1);
        break;
      case '--next':
        this.#go(1);
        break;
      case '--zoom-in':
        this.#zoomTo(this.#scale * STEP);
        break;
      case '--zoom-out':
        this.#zoomTo(this.#scale / STEP);
        break;
    }
  };

  #open(source) {
    if (!(source instanceof HTMLElement)) return;
    // Only tiles that are rendered (the chosen panel) and hold a photo take part.
    const id = CSS.escape(this.id);
    this.#items = Array.from(document.querySelectorAll(`[commandfor="${id}"][command="--open"]`)).filter(
      (tile) => tile.querySelector('img') && tile.getClientRects().length > 0
    );
    const index = this.#items.indexOf(source);
    if (index === -1) return;
    this.#trigger = source;
    this.#index = index;
    this.toggleAttribute('data-single', this.#items.length < 2);
    for (const nav of this.querySelectorAll('[data-lightbox-nav]')) nav.hidden = this.#items.length < 2;
    this.#show();
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #go(offset) {
    if (this.#items.length < 2) return;
    this.#index = (this.#index + offset + this.#items.length) % this.#items.length;
    this.#trigger = this.#items[this.#index];
    this.#show();
  }

  #show() {
    const thumb = this.#items[this.#index]?.querySelector('img');
    if (!thumb) return;
    const token = ++this.#loadToken;
    const src = thumb.currentSrc || thumb.src;
    const large = this.#sized(src, this.#largeWidth());
    // Show the tile's already-loaded photo straight away (never the previous one), then swap in the sharp version.
    this.#image.removeAttribute('srcset');
    this.#image.removeAttribute('sizes');
    this.#image.src = src;
    this.#image.alt = thumb.alt;
    if (thumb.width && thumb.height) {
      this.#image.width = thumb.width;
      this.#image.height = thumb.height;
    }
    this.#image.setAttribute('aria-busy', 'true');
    const loader = new Image();
    loader.src = large;
    const swap = () => {
      if (token !== this.#loadToken) return;
      this.#image.src = large;
      this.#image.removeAttribute('aria-busy');
    };
    (typeof loader.decode === 'function' ? loader.decode() : Promise.reject()).then(swap, () => {
      loader.complete && loader.naturalWidth ? swap() : loader.addEventListener('load', swap, { once: true });
    });
    const counter = this.querySelector('[data-lightbox-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${this.#items.length}`;
    this.#reset();
    // Warm the neighbours so stepping through feels instant.
    for (const offset of [1, -1]) {
      const next = this.#items[(this.#index + offset + this.#items.length) % this.#items.length]?.querySelector('img');
      if (next) new Image().src = this.#sized(next.currentSrc || next.src, this.#largeWidth());
    }
  }

  // One sharp size per screen, so the preloaded neighbours are exactly what gets shown.
  #largeWidth() {
    const needed = Math.max(window.innerWidth, window.innerHeight) * (window.devicePixelRatio || 1);
    return LARGE_WIDTHS.find((width) => width >= needed) ?? LARGE_WIDTHS[LARGE_WIDTHS.length - 1];
  }

  #sized(src, width) {
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  #onClose = () => {
    this.#loadToken++;
    this.#image.removeAttribute('srcset');
    this.#image.src = 'data:,';
    this.#reset();
    this.#pointers.clear();
    this.#gesture = null;
    if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: false });
  };

  #onKeydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const rtl = getComputedStyle(this.#dialog).direction === 'rtl';
    switch (event.key) {
      case 'ArrowRight':
        if (this.#scale > 1) return this.#nudge(event, -60, 0);
        event.preventDefault();
        this.#go(rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        if (this.#scale > 1) return this.#nudge(event, 60, 0);
        event.preventDefault();
        this.#go(rtl ? 1 : -1);
        break;
      case 'ArrowUp':
        if (this.#scale > 1) this.#nudge(event, 0, 60);
        break;
      case 'ArrowDown':
        if (this.#scale > 1) this.#nudge(event, 0, -60);
        break;
      case '+':
      case '=':
        event.preventDefault();
        this.#zoomTo(this.#scale * STEP);
        break;
      case '-':
      case '_':
        event.preventDefault();
        this.#zoomTo(this.#scale / STEP);
        break;
      case '0':
        event.preventDefault();
        this.#reset();
        break;
    }
  };

  #nudge(event, dx, dy) {
    event.preventDefault();
    this.#x += dx;
    this.#y += dy;
    this.#apply(true);
  }

  #onWheel = (event) => {
    event.preventDefault();
    const factor = Math.exp(-event.deltaY * (event.deltaMode === 1 ? 0.05 : 0.0025));
    this.#zoomTo(this.#scale * factor, event.clientX, event.clientY, false);
  };

  #onDoubleClick = (event) => {
    if (event.pointerType === 'touch') return;
    this.#toggleZoom(event.clientX, event.clientY);
  };

  #toggleZoom(clientX, clientY) {
    if (this.#scale > 1.01) this.#zoomTo(1);
    else this.#zoomTo(DOUBLE_TAP_SCALE, clientX, clientY);
  }

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#viewport.setPointerCapture?.(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#moved = false;
    this.#startGesture();
  };

  #startGesture() {
    const points = Array.from(this.#pointers.values());
    if (points.length >= 2) {
      const [a, b] = points;
      this.#gesture = {
        type: 'pinch',
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: this.#scale,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
        x: this.#x,
        y: this.#y,
      };
    } else if (points.length === 1) {
      this.#gesture = { type: 'drag', sx: points[0].x, sy: points[0].y, x: this.#x, y: this.#y };
    }
  }

  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = Array.from(this.#pointers.values());
    const g = this.#gesture;

    if (g.type === 'pinch' && points.length >= 2) {
      const [a, b] = points;
      const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      const next = this.#clampScale(g.scale * (distance / g.distance));
      const center = this.#center();
      // Keep the photo point under the starting pinch midpoint under the fingers.
      const ratio = next / g.scale;
      this.#scale = next;
      this.#x = cx - center.x - (g.cx - center.x - g.x) * ratio;
      this.#y = cy - center.y - (g.cy - center.y - g.y) * ratio;
      this.#moved = true;
      this.#apply(false);
      return;
    }

    if (g.type === 'drag') {
      const dx = event.clientX - g.sx;
      const dy = event.clientY - g.sy;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.#moved = true;
      if (this.#scale > 1) {
        this.#x = g.x + dx;
        this.#y = g.y + dy;
        this.#apply(false);
      } else {
        // Not zoomed: the photo follows the finger sideways as a swipe preview.
        this.#image.style.transition = 'none';
        this.#image.style.transform = `translate3d(${dx}px, 0, 0)`;
      }
    }
  };

  #onPointerUp = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    const g = this.#gesture;
    this.#pointers.delete(event.pointerId);

    if (this.#pointers.size > 0) {
      // A finger lifted mid-pinch: continue as a drag with the remaining one.
      this.#startGesture();
      return;
    }
    this.#gesture = null;

    if (g?.type === 'drag') {
      const dx = event.clientX - g.sx;
      const dy = event.clientY - g.sy;
      if (this.#scale <= 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        const rtl = getComputedStyle(this.#dialog).direction === 'rtl';
        this.#go((dx < 0) !== rtl ? 1 : -1);
        return;
      }
      if (!this.#moved) {
        const now = Date.now();
        if (event.pointerType === 'touch' && now - this.#lastTap < 300) {
          this.#lastTap = 0;
          this.#toggleZoom(event.clientX, event.clientY);
          return;
        }
        this.#lastTap = now;
        // A tap on the dark area around a not-zoomed photo closes the viewer.
        if (this.#scale <= 1 && !this.#hitsPhoto(event.clientX, event.clientY)) {
          this.#dialog.close();
          return;
        }
      }
    }
    if (this.#scale < 1.01) this.#reset();
    else this.#apply(true);
  };

  #hitsPhoto(clientX, clientY) {
    const box = this.#contentBox();
    return clientX >= box.left && clientX <= box.right && clientY >= box.top && clientY <= box.bottom;
  }

  // The visible photo area inside the object-contain image box at scale 1.
  #contentBox() {
    const rect = this.#viewport.getBoundingClientRect();
    const naturalW = this.#image.naturalWidth || this.#image.width || rect.width;
    const naturalH = this.#image.naturalHeight || this.#image.height || rect.height;
    const fit = Math.min(rect.width / naturalW, rect.height / naturalH);
    const width = naturalW * fit;
    const height = naturalH * fit;
    const left = rect.left + (rect.width - width) / 2;
    const top = rect.top + (rect.height - height) / 2;
    return { left, top, width, height, right: left + width, bottom: top + height };
  }

  #center() {
    const rect = this.#viewport.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  #clampScale(value) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  }

  #zoomTo(value, clientX, clientY, animate = true) {
    const next = this.#clampScale(value);
    const center = this.#center();
    const px = (clientX ?? center.x) - center.x;
    const py = (clientY ?? center.y) - center.y;
    const ratio = next / this.#scale;
    // Zoom around the pointer so the spot under it stays put.
    this.#x = px - (px - this.#x) * ratio;
    this.#y = py - (py - this.#y) * ratio;
    this.#scale = next;
    if (next <= 1) {
      this.#x = 0;
      this.#y = 0;
    }
    this.#apply(animate);
  }

  #reset() {
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.#apply(false);
  }

  #apply(animate) {
    // Keep the zoomed photo covering the screen: no panning past its edges.
    const box = this.#contentBox();
    const rect = this.#viewport.getBoundingClientRect();
    const maxX = Math.max(0, (box.width * this.#scale - rect.width) / 2);
    const maxY = Math.max(0, (box.height * this.#scale - rect.height) / 2);
    this.#x = Math.min(maxX, Math.max(-maxX, this.#x));
    this.#y = Math.min(maxY, Math.max(-maxY, this.#y));

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#image.style.transition = animate && !reduceMotion ? 'transform 200ms cubic-bezier(0.2, 0, 0, 1)' : 'none';
    this.#image.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.#scale})`;
    this.#image.toggleAttribute('data-zoomed', this.#scale > 1.01);

    const label = this.querySelector('[data-lightbox-scale]');
    if (label) label.textContent = `${Math.round(this.#scale * 100)}%`;
    const zoomIn = this.querySelector('[data-lightbox-zoom-in]');
    const zoomOut = this.querySelector('[data-lightbox-zoom-out]');
    if (zoomIn) zoomIn.disabled = this.#scale >= MAX_SCALE - 0.001;
    if (zoomOut) zoomOut.disabled = this.#scale <= MIN_SCALE + 0.001;
  }
}

if (!customElements.get('s-gallery-lightbox')) {
  customElements.define('s-gallery-lightbox', SGalleryLightbox);
}
